import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SiteNav from './SiteNav';
import BlogFooter from './BlogFooter';
import SEO from './SEO';

// ─── Composants internes ──────────────────────────────────────────────────────

function LegalNav() {
  return (
    <div className="mt-16 pt-8 border-t border-ink/10 text-center">
      <p className="text-sm text-taupe/60 mb-4">Voir aussi :</p>
      <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
        <Link to="/fr/confidentialite" className="text-sm text-amber hover:underline transition-colors font-medium">
          Confidentialité
        </Link>
        <span className="text-ink/20 text-xs">|</span>
        <span className="text-sm text-ink/40 font-medium cursor-default">
          Conditions d'utilisation
        </span>
        <span className="text-ink/20 text-xs">|</span>
        <Link to="/fr/mentions-legales" className="text-sm text-amber hover:underline transition-colors font-medium">
          Mentions légales
        </Link>
      </nav>
    </div>
  );
}

function ArticleLabel({ number }: { number: string }) {
  return (
    <span className="block text-[11px] tracking-[2px] uppercase text-amber font-medium mb-2">
      Article {number}
    </span>
  );
}

function ArticleTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-serif text-2xl font-normal text-ink mb-4 leading-snug">
      {children}
    </h2>
  );
}

function SubTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="font-serif text-lg font-normal text-ink mb-3 mt-6">
      {children}
    </h3>
  );
}

function Body({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={`text-base text-ink/80 leading-relaxed ${className}`}>
      {children}
    </p>
  );
}

function InfoBlock({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-sand/20 border-l-4 border-amber px-5 py-4 rounded-r-sm space-y-1.5 my-4">
      {children}
    </div>
  );
}

function Separator() {
  return <div className="border-b border-ink/15 my-10" />;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ConditionsUtilisation() {
  return (
    <>
      <SEO
        title="Conditions d'utilisation | Amargi"
        description="Conditions générales d'utilisation de la plateforme Amargi — règles d'accès, responsabilités et cadre légal de nos outils d'évaluation somatique."
        path="/conditions-utilisation"
      />
      <div className="min-h-screen bg-paper font-sans">
      <SiteNav />
      <main className="pt-20 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">

          {/* Retour */}
          <Link
            to="/fr/"
            className="inline-flex items-center gap-2 text-taupe/60 hover:text-amber text-sm mb-10 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au site
          </Link>

          {/* En-tête */}
          <div className="pb-10 border-b border-ink/15 mb-12">
            <span className="block text-[11px] tracking-[2px] uppercase text-amber mb-3">
              Informations légales
            </span>
            <h1 className="font-serif text-4xl font-light text-ink mb-3 leading-tight">
              Conditions générales d&apos;utilisation
            </h1>
            <p className="text-sm text-taupe/60 italic">Dernière mise à jour : juin 2026</p>
          </div>

          {/* Préambule */}
          <div className="mb-10">
            <span className="block text-[11px] tracking-[2px] uppercase text-amber font-medium mb-2">
              Préambule
            </span>
            <Body className="mb-4">
              Les présentes Conditions Générales d&apos;Utilisation (ci-après « CGU ») régissent l&apos;accès et l&apos;utilisation du site amargi.me et de l&apos;outil Amargi (ci-après « le Service »), édités par Above The Clouds (Bertrand Jacob), Rue de la Hulpe 105A, 1331 Rosières, Belgique (BCE : BE 1022.831.049).
            </Body>
            <Body>
              En accédant au Service, en créant un compte ou en utilisant l&apos;outil Amargi, vous acceptez sans réserve les présentes CGU. Si vous n&apos;acceptez pas ces conditions, vous devez cesser d&apos;utiliser le Service.
            </Body>
          </div>

          <Separator />

          {/* ── Article 01 ── Description du Service ─────────────────────── */}
          <article className="mb-10">
            <ArticleLabel number="01" />
            <ArticleTitle>Description du Service</ArticleTitle>
            <Body className="mb-4">
              Amargi est un outil de régulation somatique guidé par l&apos;intelligence artificielle. Il propose des protocoles personnalisés de respiration, de mouvement et d&apos;ancrage sensoriel, fondés sur la théorie polyvagale et les neurosciences du stress.
            </Body>
            <div className="bg-amber/10 border border-amber/30 rounded-sm px-5 py-4 mb-4">
              <p className="text-sm text-ink/80 leading-relaxed">
                <strong>Nature du Service :</strong> Amargi est un outil de bien-être à vocation éducative et de coaching. Il ne constitue en aucun cas un dispositif médical, un outil de diagnostic clinique, une thérapie psychologique ou un traitement de trouble mental ou physique. Les contenus proposés ne remplacent pas l&apos;avis d&apos;un professionnel de santé qualifié.
              </p>
            </div>
            <Body>
              Le Service est accessible via le site web amargi.me et peut être utilisé sans inscription (bilan sensoriel gratuit) ou avec un compte utilisateur (accès aux fonctionnalités complètes selon l&apos;offre souscrite).
            </Body>
          </article>

          <Separator />

          {/* ── Article 02 ── Accès et création de compte ────────────────── */}
          <article className="mb-10">
            <ArticleLabel number="02" />
            <ArticleTitle>Accès au Service et création de compte</ArticleTitle>

            <SubTitle>Conditions d&apos;accès</SubTitle>
            <Body className="mb-5">
              L&apos;accès au Service est réservé aux personnes physiques majeures (18 ans ou plus). En créant un compte, vous déclarez avoir l&apos;âge requis et la capacité juridique pour accepter les présentes CGU.
            </Body>

            <SubTitle>Création de compte</SubTitle>
            <Body className="mb-4">
              Pour accéder aux fonctionnalités complètes du Service, vous devez créer un compte en fournissant une adresse e-mail valide. Vous êtes responsable de la confidentialité de vos identifiants de connexion et de toute activité réalisée depuis votre compte.
            </Body>
            <Body className="mb-5">
              En cas de perte ou de compromission de vos identifiants, vous vous engagez à en informer Above The Clouds dans les meilleurs délais à l&apos;adresse{' '}
              <a href="mailto:contact@amargi.me" className="text-amber hover:underline transition-colors font-medium">
                contact@amargi.me
              </a>.
            </Body>

            <SubTitle>Utilisation sans compte</SubTitle>
            <Body>
              Un accès partiel au Service (bilan sensoriel gratuit) est possible sans création de compte. Dans ce cas, un identifiant anonyme est stocké dans votre navigateur (localStorage) pour lier vos sessions. Cet identifiant est supprimable à tout moment en effaçant les données de votre navigateur.
            </Body>
          </article>

          <Separator />

          {/* ── Article 03 ── Offres et tarifs ───────────────────────────── */}
          <article className="mb-10">
            <ArticleLabel number="03" />
            <ArticleTitle>Offres et conditions tarifaires</ArticleTitle>

            <SubTitle>Offre gratuite</SubTitle>
            <Body className="mb-5">
              L&apos;accès au bilan sensoriel de base est gratuit, sans inscription et sans limitation dans le temps.
            </Body>

            <SubTitle>Offres payantes</SubTitle>
            <Body className="mb-4">
              Des offres payantes donnant accès à des fonctionnalités avancées (suivi de l&apos;évolution, historique de sessions, exercices personnalisés, etc.) sont proposées. Les tarifs en vigueur sont consultables sur la page{' '}
              <Link to="/fr/tarifs" className="text-amber hover:underline transition-colors font-medium">
                Tarifs
              </Link>{' '}
              du site.
            </Body>

            <SubTitle>Paiement</SubTitle>
            <Body className="mb-5">
              Le paiement des offres payantes est traité exclusivement par Mollie B.V. Above The Clouds ne conserve aucune donnée bancaire. En procédant au paiement, vous acceptez également les{' '}
              <a href="https://www.mollie.com/fr/legal" target="_blank" rel="noopener noreferrer" className="text-amber hover:underline transition-colors font-medium">
                conditions d&apos;utilisation de Mollie
              </a>.
            </Body>

            <SubTitle>Droit de rétractation</SubTitle>
            <InfoBlock>
              <p className="text-base text-ink/80">
                Conformément à la directive européenne 2011/83/UE et au Code de droit économique belge, vous disposez d&apos;un droit de rétractation de <strong>14 jours calendaires</strong> à compter de la souscription d&apos;un abonnement payant, sans avoir à justifier votre décision.
              </p>
              <p className="text-base text-ink/80">
                Pour exercer ce droit, contactez-nous à{' '}
                <a href="mailto:contact@amargi.me" className="text-amber hover:underline transition-colors font-medium">
                  contact@amargi.me
                </a>.
              </p>
              <p className="text-base text-ink/80">
                Ce droit ne s&apos;applique pas si vous avez expressément demandé à bénéficier du service avant l&apos;expiration du délai de rétractation et que le service a été pleinement exécuté.
              </p>
            </InfoBlock>
          </article>

          <Separator />

          {/* ── Article 04 ── Obligations de l'utilisateur ───────────────── */}
          <article className="mb-10">
            <ArticleLabel number="04" />
            <ArticleTitle>Obligations de l&apos;utilisateur</ArticleTitle>
            <Body className="mb-4">En utilisant le Service, vous vous engagez à :</Body>
            <div className="bg-sand/20 border-l-4 border-amber px-5 py-4 rounded-r-sm space-y-2 my-4">
              <p className="text-base text-ink/80">Utiliser le Service conformément à sa destination et aux présentes CGU.</p>
              <p className="text-base text-ink/80">Ne pas tenter d&apos;accéder de manière non autorisée aux systèmes informatiques du Service.</p>
              <p className="text-base text-ink/80">Ne pas utiliser le Service à des fins illicites ou contraires à l&apos;ordre public.</p>
              <p className="text-base text-ink/80">Ne pas transmettre de contenus illicites, diffamatoires, ou portant atteinte aux droits de tiers.</p>
              <p className="text-base text-ink/80">Ne pas perturber le bon fonctionnement du Service par quelque moyen que ce soit.</p>
              <p className="text-base text-ink/80">Ne pas reproduire, copier, vendre ou exploiter commercialement tout ou partie du Service sans autorisation écrite préalable d&apos;Above The Clouds.</p>
            </div>
          </article>

          <Separator />

          {/* ── Article 05 ── Propriété intellectuelle ───────────────────── */}
          <article className="mb-10">
            <ArticleLabel number="05" />
            <ArticleTitle>Propriété intellectuelle</ArticleTitle>
            <Body className="mb-4">
              L&apos;ensemble des contenus du Service (textes, images, logos, protocoles, algorithmes, code source, etc.) est la propriété exclusive d&apos;Above The Clouds ou de ses partenaires, et est protégé par le droit belge et international de la propriété intellectuelle.
            </Body>
            <Body className="mb-4">
              Toute reproduction, représentation, modification, publication ou adaptation de tout ou partie de ces éléments est strictement interdite sans l&apos;autorisation écrite préalable d&apos;Above The Clouds.
            </Body>
            <Body>
              Les références scientifiques citées (Porges, Panjabi, Damasio, van der Kolk, etc.) sont utilisées à des fins pédagogiques. Elles ne constituent pas une approbation de ces auteurs envers l&apos;outil Amargi.
            </Body>
          </article>

          <Separator />

          {/* ── Article 06 ── Limitation de responsabilité ───────────────── */}
          <article className="mb-10">
            <ArticleLabel number="06" />
            <ArticleTitle>Limitation de responsabilité</ArticleTitle>

            <SubTitle>Disponibilité du Service</SubTitle>
            <Body className="mb-5">
              Above The Clouds s&apos;efforce d&apos;assurer la disponibilité du Service 24h/24 et 7j/7, mais ne peut garantir une disponibilité sans interruption. Des maintenances planifiées ou des incidents techniques peuvent survenir. Above The Clouds ne saurait être tenu responsable des dommages résultant d&apos;une indisponibilité temporaire du Service.
            </Body>

            <SubTitle>Nature du contenu</SubTitle>
            <Body className="mb-5">
              Le Service est un outil de bien-être à vocation éducative. Above The Clouds ne saurait être tenu responsable de toute conséquence directe ou indirecte résultant de l&apos;utilisation du Service, notamment en cas de non-amélioration de l&apos;état de santé perçu ou de tout effet indésirable. En cas de symptômes physiques ou psychologiques persistants, consultez un professionnel de santé.
            </Body>

            <SubTitle>Liens hypertextes</SubTitle>
            <Body>
              Le Service peut contenir des liens vers des sites tiers. Above The Clouds n&apos;exerce aucun contrôle sur ces sites et décline toute responsabilité quant à leur contenu.
            </Body>
          </article>

          <Separator />

          {/* ── Article 07 ── Résiliation ─────────────────────────────────── */}
          <article className="mb-10">
            <ArticleLabel number="07" />
            <ArticleTitle>Résiliation et suppression de compte</ArticleTitle>

            <SubTitle>Par l&apos;utilisateur</SubTitle>
            <Body className="mb-4">
              Vous pouvez résilier votre abonnement à tout moment depuis votre espace personnel ou en contactant{' '}
              <a href="mailto:contact@amargi.me" className="text-amber hover:underline transition-colors font-medium">
                contact@amargi.me
              </a>. La résiliation prend effet à la fin de la période d&apos;abonnement en cours. Aucun remboursement prorata n&apos;est effectué, sauf exercice du droit de rétractation dans le délai légal.
            </Body>
            <Body className="mb-5">
              Vous pouvez demander la suppression définitive de votre compte et de vos données à tout moment en écrivant à{' '}
              <a href="mailto:contact@amargi.me" className="text-amber hover:underline transition-colors font-medium">
                contact@amargi.me
              </a>. La suppression est effective dans un délai de 30 jours.
            </Body>

            <SubTitle>Par Above The Clouds</SubTitle>
            <Body>
              Above The Clouds se réserve le droit de suspendre ou de supprimer un compte en cas de violation des présentes CGU, d&apos;utilisation frauduleuse du Service, ou de non-paiement d&apos;un abonnement, après mise en demeure restée sans effet.
            </Body>
          </article>

          <Separator />

          {/* ── Article 08 ── Données personnelles ───────────────────────── */}
          <article className="mb-10">
            <ArticleLabel number="08" />
            <ArticleTitle>Données personnelles</ArticleTitle>
            <Body>
              Le traitement de vos données personnelles dans le cadre de l&apos;utilisation du Service est régi par notre{' '}
              <Link to="/fr/confidentialite" className="text-amber hover:underline transition-colors font-medium">
                Politique de confidentialité
              </Link>, distincte des présentes CGU et conforme au RGPD (UE) 2016/679.
            </Body>
          </article>

          <Separator />

          {/* ── Article 09 ── Modifications des CGU ──────────────────────── */}
          <article className="mb-10">
            <ArticleLabel number="09" />
            <ArticleTitle>Modifications des CGU</ArticleTitle>
            <Body>
              Above The Clouds se réserve le droit de modifier les présentes CGU à tout moment. La version en vigueur est celle accessible sur cette page. En cas de modification substantielle, les utilisateurs disposant d&apos;un compte seront informés par e-mail au moins 30 jours avant l&apos;entrée en vigueur des nouvelles conditions. La poursuite de l&apos;utilisation du Service après cette date vaut acceptation des nouvelles CGU.
            </Body>
          </article>

          <Separator />

          {/* ── Article 10 ── Droit applicable ───────────────────────────── */}
          <article className="mb-10">
            <ArticleLabel number="10" />
            <ArticleTitle>Droit applicable et juridiction compétente</ArticleTitle>
            <Body>
              Les présentes CGU sont régies par le droit belge. En cas de litige relatif à leur interprétation ou à leur exécution, et à défaut de résolution amiable dans un délai de 30 jours, les tribunaux compétents de l&apos;arrondissement judiciaire de Bruxelles seront seuls compétents.
            </Body>
          </article>

          <Separator />

          {/* ── Article 11 ── Contact ─────────────────────────────────────── */}
          <article className="mb-12">
            <ArticleLabel number="11" />
            <ArticleTitle>Contact</ArticleTitle>
            <Body className="mb-4">Pour toute question relative aux présentes CGU :</Body>
            <div className="border border-ink/20 rounded-md px-7 py-6 space-y-1.5">
              <p className="text-ink text-base font-medium">Above The Clouds</p>
              <p className="text-ink/70 text-base">Bertrand Jacob</p>
              <p className="text-ink/70 text-base">Rue de la Hulpe 105A, 1331 Rosières, Belgique</p>
              <p className="text-ink/70 text-base">
                E-mail :{' '}
                <a href="mailto:contact@amargi.me" className="text-amber hover:underline transition-colors font-medium">
                  contact@amargi.me
                </a>
              </p>
              <p className="text-ink/50 text-sm mt-2">Délai de réponse : 30 jours maximum (RGPD, Art. 12).</p>
            </div>
          </article>

          {/* Note de bas de page */}
          <div className="bg-amber/10 border border-amber/30 rounded-sm px-5 py-4 text-center mb-8">
            <p className="text-sm text-taupe/70 leading-relaxed italic">
              Ces Conditions Générales d&apos;Utilisation ont été rédigées conformément au droit belge et au droit européen applicable (directive 2011/83/UE, RGPD, directive eCommerce 2000/31/CE).
            </p>
          </div>

          {/* Navigation croisée */}
          <LegalNav />

        </div>
      </main>
      <BlogFooter />
    </div>
    </>
  );
}
